/**
 * CypSafeguardingNoticeDialog.js
 *
 * A dismissable notice shown to a parent/carer immediately after they
 * select a flagged safeguarding option on the CYP ADHD riskBehaviours
 * question. It reassures the respondent that the clinical team has been
 * informed and provides emergency contact information.
 *
 * Key design differences from the Adult ADHD SelfHarmCrisisSupportDialog:
 *
 *   - **Dismissable**: uses Radix Dialog (not AlertDialog), so clicking
 *     outside the panel, pressing Escape, or clicking the Continue button
 *     all close it. The notice should not block the respondent.
 *
 *   - **No SLaM-specific content**: the emergency line is generic NHS
 *     guidance (GP / 111 / 999), not SLaM crisis services.
 *
 *   - **Personalised copy**: the body includes the organisation name and
 *     the child's name (via props), matching the Python email template.
 *
 *   - **Gold Continue CTA**: service-user surface, same as other CYP forms.
 *
 * The copy constants are imported from cypSafeguardingAlert.js which is the
 * JS single source of truth, mirrored from the Python
 * constants/cyp_adhd/safeguarding.py. Changes require Tom (clinical) and
 * Jasmine (comprehension) sign-off.
 *
 * Props:
 *   isOpen        — boolean controlling visibility
 *   onClose       — callback when the dialog is dismissed by any means
 *   organisation  — organisation display name for the body copy
 *   childName     — child's first name for the emergency line
 */

import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  CYP_SAFEGUARDING_NOTICE_TITLE,
  CYP_SAFEGUARDING_NOTICE_BODY,
  CYP_SAFEGUARDING_NOTICE_EMERGENCY,
} from '../utils/cypSafeguardingAlert';

export default function CypSafeguardingNoticeDialog({
  isOpen,
  onClose,
  organisation = 'your healthcare provider',
  childName = 'your child',
}) {
  // Resolve the {organisation} and {child_name} tokens in the copy.
  const bodyText = CYP_SAFEGUARDING_NOTICE_BODY.replace(
    '{organisation}',
    organisation,
  );
  const emergencyText = CYP_SAFEGUARDING_NOTICE_EMERGENCY.replace(
    '{child_name}',
    childName,
  );

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <Dialog.Portal>
        {/* Overlay — consistent with other service-user dialogs */}
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />

        {/* Content panel — centred, scrollable, max-w-lg (shorter copy
            than the SLaM dialog, so a narrower panel is appropriate) */}
        <Dialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-lg max-h-[90vh] overflow-y-auto z-50"
          aria-describedby="cyp-safeguarding-notice-body"
        >
          {/* Title — required for accessibility */}
          <Dialog.Title className="text-lg font-semibold text-[#2A1A28] mb-4">
            {CYP_SAFEGUARDING_NOTICE_TITLE}
          </Dialog.Title>

          {/* Body — personalised reassurance + emergency line */}
          <div
            id="cyp-safeguarding-notice-body"
            className="text-[#2A1A28] text-base leading-relaxed space-y-4"
          >
            <p>{bodyText}</p>
            <p className="font-semibold">{emergencyText}</p>
          </div>

          {/* Primary CTA — gold fill, plum label (service-user surface).
              44px minimum touch target per design rules. */}
          <div className="mt-8 flex justify-end">
            <Dialog.Close asChild>
              <button
                className="inline-flex items-center justify-center px-6 py-3 min-h-[44px] rounded-lg bg-[#E7C22F] text-[#43213E] font-semibold text-base transition-colors hover:bg-[#d4b129] focus:outline-none focus:ring-2 focus:ring-[#43213E] focus:ring-offset-2"
              >
                Continue
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
